// A page cannot write the Photos library by itself.
// iPhone/iPad: the share sheet's Save Image is the path into Photos.
// That call has to start in the same turn as the camera change or a later tap.
// Android: a download of the same bytes is scanned into the gallery (Downloads).
// Photos picked from the gallery are already on the device; callers skip those.

function isIos(nav) {
  if (!nav) return false;
  const ua = String(nav.userAgent || '');
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return nav.platform === 'MacIntel' && Number(nav.maxTouchPoints) > 1;
}

function isAndroid(nav) {
  if (!nav) return false;
  return /Android/i.test(String(nav.userAgent || ''));
}

function keepsCameraPhotoOnDevice(nav) {
  return isIos(nav) || isAndroid(nav);
}

function cameraPhotoExtension(file) {
  const type = String((file && file.type) || '').toLowerCase();
  const name = String((file && file.name) || '');
  if (type.includes('png') || /\.png$/i.test(name)) return '.png';
  if (type.includes('webp') || /\.webp$/i.test(name)) return '.webp';
  if (type.includes('gif') || /\.gif$/i.test(name)) return '.gif';
  if (type.includes('heic') || /\.heic$/i.test(name)) return '.heic';
  if (type.includes('heif') || /\.heif$/i.test(name)) return '.heif';
  return '.jpg';
}

function cameraPhotoType(file) {
  const type = String((file && file.type) || '').toLowerCase();
  if (type.startsWith('image/')) return file.type;
  const ext = cameraPhotoExtension(file);
  if (ext === '.png') return 'image/png';
  if (ext === '.webp') return 'image/webp';
  if (ext === '.gif') return 'image/gif';
  if (ext === '.heic') return 'image/heic';
  if (ext === '.heif') return 'image/heif';
  return 'image/jpeg';
}

function cameraPhotoFile(file, workOrderId, now = Date.now()) {
  const type = cameraPhotoType(file);
  const ext = cameraPhotoExtension({ type, name: file && file.name });
  const id = workOrderId ? String(workOrderId) : 'photo';
  const stamp = Number.isFinite(Number(now)) ? Number(now) : Date.now();
  const name = `WO-${id}-${stamp}${ext}`;
  return new File([file], name, {
    type,
    lastModified: (file && file.lastModified) || stamp,
  });
}

function canShareFile(nav, file) {
  if (
    !nav ||
    typeof nav.share !== 'function' ||
    typeof nav.canShare !== 'function'
  ) {
    return false;
  }
  try {
    return nav.canShare({ files: [file] }) === true;
  } catch {
    return false;
  }
}

function shareFailure(error, filename) {
  const name = error && error.name;
  if (name === 'AbortError') {
    return { ok: false, declined: true, method: 'share', filename };
  }
  return { ok: false, retry: true, method: 'share', filename, error };
}

function downloadCameraPhoto(file, deps) {
  const doc =
    deps.document || (typeof document !== 'undefined' ? document : null);
  try {
    if (!doc || typeof doc.createElement !== 'function') {
      return { ok: false, error: new Error('no document') };
    }
    const view = doc.defaultView || globalThis;
    const urlApi = view.URL;
    if (!urlApi || typeof urlApi.createObjectURL !== 'function') {
      return { ok: false, error: new Error('no object url') };
    }
    const url = urlApi.createObjectURL(file);
    const link = doc.createElement('a');
    link.href = url;
    link.download = file.name;
    link.rel = 'noopener';
    if (doc.body && typeof doc.body.appendChild === 'function') {
      doc.body.appendChild(link);
    }
    link.click();
    if (typeof link.remove === 'function') link.remove();
    const revoke = () => {
      if (typeof urlApi.revokeObjectURL === 'function')
        urlApi.revokeObjectURL(url);
    };
    if (typeof deps.schedule === 'function') deps.schedule(revoke, 1500);
    else setTimeout(revoke, 1500);
    return { ok: true, method: 'download', filename: file.name };
  } catch (error) {
    return {
      ok: false,
      retry: true,
      method: 'download',
      filename: file.name,
      error,
    };
  }
}

function saveCameraPhotoToGallery(file, deps = {}) {
  const nav =
    deps.navigator || (typeof navigator !== 'undefined' ? navigator : null);
  if (!file || !keepsCameraPhotoOnDevice(nav)) {
    return Promise.resolve({
      ok: true,
      method: 'skip',
      filename: file && file.name,
    });
  }

  if (isIos(nav) && canShareFile(nav, file)) {
    try {
      const pending = nav.share({
        files: [file],
        title: 'Save photo',
      });
      return Promise.resolve(pending).then(
        () => ({ ok: true, method: 'share', filename: file.name }),
        (error) => shareFailure(error, file.name),
      );
    } catch (error) {
      return Promise.resolve(shareFailure(error, file.name));
    }
  }

  const downloaded = downloadCameraPhoto(file, deps);
  if (downloaded.ok && isIos(nav)) {
    return Promise.resolve({ ...downloaded, destination: 'files' });
  }
  if (downloaded.ok) {
    return Promise.resolve({ ...downloaded, destination: 'gallery' });
  }
  return Promise.resolve(downloaded);
}

function gallerySaveState(result) {
  if (!result || result.method === 'skip' || result.ok) return 'saved';
  if (result.declined) return 'declined';
  return 'needs-tap';
}

function gallerySaveMessage(result) {
  if (!result || result.method === 'skip' || result.declined) return '';
  if (result.ok && result.method === 'share') return '';
  if (result.ok && result.destination === 'files') {
    return 'A copy was saved in Files on this phone.';
  }
  if (result.ok && result.method === 'download') {
    return 'A copy was saved on this phone.';
  }
  if (result.retry) {
    return 'Tap Save a copy to keep this photo on your phone.';
  }
  return '';
}

module.exports = {
  isIos,
  keepsCameraPhotoOnDevice,
  cameraPhotoFile,
  saveCameraPhotoToGallery,
  gallerySaveState,
  gallerySaveMessage,
};
